import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { TeamService, LeagueService, SeasonService } from '../../services/firebaseService';
import { getUserClaimedTeams } from '../../utils/teamUtils';
import PredictionResults from '../../components/player/PredictionResults';

export default function PredictionHistory() {
  const { teamId } = useParams();
  const { userProfile } = useAuth();
  const [team, setTeam] = useState(null);
  const [league, setLeague] = useState(null);
  const [season, setSeason] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (teamId && userProfile) {
      loadTeamData();
    }
  }, [teamId, userProfile]);

  const loadTeamData = async () => {
    try {
      setLoading(true);
      setError('');

      // Find which league/season this team belongs to from user's claimed teams
      const claimedTeams = getUserClaimedTeams(userProfile.teams || {});
      const claimedTeam = claimedTeams.find(ct => ct.teamId === teamId);

      if (!claimedTeam) {
        throw new Error('Team not found in your claimed teams');
      }

      // Load team, league, and season data
      const [teamData, leagueData, seasonData] = await Promise.all([
        TeamService.getTeam(claimedTeam.leagueId, claimedTeam.seasonId, teamId),
        LeagueService.getLeague(claimedTeam.leagueId),
        SeasonService.getSeason(claimedTeam.seasonId)
      ]);

      if (!teamData || !leagueData || !seasonData) {
        throw new Error('Team, league, or season not found');
      }

      setTeam(teamData);
      setLeague(leagueData);
      setSeason(seasonData);

    } catch (err) {
      console.error('Error loading team data:', err);
      setError(err.message || 'Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-survivor-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <p className="text-red-600 mb-4">{error}</p>
          <Link
            to="/"
            className="text-survivor-600 hover:text-survivor-700"
          >
            Return to Homepage
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/"
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Homepage
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Prediction History</h1>
          <p className="text-gray-600 mt-1">
            {team?.name} - {league?.name}
          </p>
        </div>

        {/* Prediction Results Component */}
        <PredictionResults
          leagueId={league.id}
          seasonId={season.id}
          teamId={teamId}
          showHeader={false}
        />
      </div>
    </div>
  );
}

